/**
 * Error Handling Utilities
 * Centralized error handling for HTTP requests following DRY principle
 */

/**
 * HTTP error response interface
 */
export interface HttpErrorResponse {
  message: string;
  status: number;
  statusText: string;
  data?: any;
}

/**
 * Parse error response from backend API
 * Attempts to extract error message from various response formats
 */
export const parseErrorResponse = async (
  response: Response
): Promise<HttpErrorResponse> => {
  let errorMessage = `HTTP error! status: ${response.status}`;
  let errorData: any = {};

  // Try to parse error response from backend
  try {
    const errorText = await response.text();
    if (errorText) {
      errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
    }
  } catch (parseError) {
    // If parsing fails, use default error message
    console.error("Failed to parse error response:", parseError);
  }

  return {
    message: errorMessage,
    status: response.status,
    statusText: response.statusText,
    data: errorData,
  };
};

/**
 * Get user-friendly error message based on HTTP status code
 */
export const getHttpErrorMessage = (status: number): string => {
  switch (status) {
    case 400:
      return "Неверный запрос. Проверьте данные и попробуйте снова.";
    case 401:
      return "Необходима авторизация для выполнения этого действия.";
    case 403:
      return "У вас нет прав для выполнения этого действия.";
    case 404:
      return "Запрашиваемый ресурс не найден.";
    case 409:
      return "Конфликт данных. Возможно, ресурс используется в других местах.";
    case 422:
      return "Ошибка валидации данных.";
    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
      return "Внутренняя ошибка сервера. Попробуйте позже.";
    default:
      return `HTTP error! status: ${status}`;
  }
};

/**
 * Get user-friendly delete error message based on HTTP status code
 */
export const getDeleteErrorMessage = (
  status: number,
  resourceName: string = "ресурс"
): string => {
  switch (status) {
    case 404:
      return `${resourceName} не найден. Возможно, он уже удален.`;
    case 409:
      return `Конфликт данных. Возможно, ${resourceName} используется в других местах.`;
    default:
      return getHttpErrorMessage(status);
  }
};

/**
 * Handle HTTP error response
 * Parses the response and creates a detailed error object
 */
export const handleHttpError = async (
  response: Response,
  customMessages?: Partial<Record<number, string>>
): Promise<never> => {
  const errorResponse = await parseErrorResponse(response);

  // Use custom message if provided, otherwise use default
  const errorMessage =
    customMessages?.[response.status] ||
    errorResponse.message ||
    getHttpErrorMessage(response.status);

  const error = new Error(errorMessage);
  (error as any).data = errorResponse.data;
  (error as any).status = response.status;
  (error as any).statusText = response.statusText;

  throw error;
};

/**
 * Simplified error handler for delete operations
 */
export const handleDeleteError = async (
  response: Response,
  resourceName: string = "ресурс"
): Promise<never> => {
  const errorResponse = await parseErrorResponse(response);

  const errorMessage =
    errorResponse.message || getDeleteErrorMessage(response.status, resourceName);

  const error = new Error(errorMessage);
  (error as any).data = errorResponse.data;
  (error as any).status = response.status;
  (error as any).statusText = response.statusText;

  throw error;
};

/**
 * Extract error message from any error type
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage: string = "Произошла ошибка"
): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object" && "message" in error) {
    return String((error as any).message);
  }
  return defaultMessage;
};

